import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Divider,
    Grid,
    Stack,
    Typography,
} from '@mui/material'
import {
    ArrowForward as ArrowForwardIcon,
    Campaign as CampaignIcon,
    Download as DownloadIcon,
    Mail as MailIcon,
    Newspaper as NewspaperIcon,
    Verified as VerifiedIcon,
} from '@mui/icons-material'
import { useLanguage } from '../contexts/LanguageContext'

const ORANGE = '#FF8A00'

const cardSx = {
    borderRadius: 6,
    background: 'rgba(16, 11, 38, 0.92)',
    border: '1px solid rgba(255,255,255,0.08)',
    boxShadow: '0 24px 60px rgba(0,0,0,0.22)',
}

const Highlight = ({ text }) => (
    <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'flex-start' }}>
        <Box sx={{ width: 10, height: 10, borderRadius: 999, bgcolor: ORANGE, mt: '5px', flex: '0 0 auto' }} />
        <Typography sx={{ color: 'rgba(255,255,255,0.76)' }}>{text}</Typography>
    </Box>
)

const Fact = ({ label, value }) => (
    <Box sx={{ p: 1.4, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.56)', fontWeight: 800, fontSize: 12.5 }}>{label}</Typography>
        <Typography sx={{ color: '#fff', fontWeight: 900, mt: 0.5 }}>{value}</Typography>
    </Box>
)

const ContactRow = ({ label, value }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.56)', fontWeight: 800 }}>{label}</Typography>
        <Typography sx={{ color: '#fff', fontWeight: 800, textAlign: 'right' }}>{value}</Typography>
    </Box>
)

const Press = () => {
    const navigate = useNavigate()
    const { lang } = useLanguage()
    const isFr = lang === 'fr'

    const mediaAngles = isFr
        ? [
            'Paiements finances par la diaspora vers les marches africains',
            'Tarification locale prioritaire concue pour le volume et l accessibilite',
            'Outils marchands pour factures, taxes, ticketing, et encaissements',
            'Recus et pistes de preuve integres a chaque grand parcours',
        ]
        : [
            'Diaspora-funded payments into African markets',
            'Local-first pricing designed for volume and accessibility',
            'Merchant tools for invoices, tax, ticketing, and collections',
            'Receipts and proof trails built into every major flow',
        ]

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#09061B', py: { xs: 4, md: 6 } }}>
            <Container maxWidth="lg">
                <Card
                    sx={{
                        borderRadius: 6,
                        mb: 3,
                        overflow: 'hidden',
                        background:
                            'linear-gradient(135deg, rgba(17,10,40,0.98) 0%, rgba(70,36,188,0.96) 58%, rgba(255,138,0,0.16) 100%)',
                        border: '1px solid rgba(255,255,255,0.08)',
                        boxShadow: '0 30px 80px rgba(0,0,0,0.28)',
                    }}
                >
                    <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                        <Stack spacing={1.5}>
                            <Stack direction="row" spacing={1.2} alignItems="center">
                                <Avatar
                                    sx={{
                                        width: 42,
                                        height: 42,
                                        bgcolor: 'rgba(255,138,0,0.12)',
                                        border: '1px solid rgba(255,138,0,0.24)',
                                    }}
                                >
                                    <NewspaperIcon sx={{ color: ORANGE }} />
                                </Avatar>
                                <Chip
                                    label={isFr ? 'Salle de presse MojoPayr' : 'MojoPayr Newsroom'}
                                    sx={{
                                        bgcolor: 'rgba(255,255,255,0.12)',
                                        color: '#fff',
                                        fontWeight: 900,
                                    }}
                                />
                            </Stack>

                            <Typography sx={{ color: '#fff', fontWeight: 950, fontSize: { xs: 30, md: 44 } }}>
                                {isFr ? 'Presse' : 'Press'}
                            </Typography>

                            <Typography sx={{ color: 'rgba(255,255,255,0.82)', maxWidth: 860 }}>
                                {isFr
                                    ? 'Informations media officielles de MojoPayr, messages de lancement, faits d entreprise, et points de contact pour journalistes, partenaires, et publications du secteur.'
                                    : 'Official MojoPayr media information, launch messaging, company facts, and contact points for journalists, partners, and industry publications.'}
                            </Typography>

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ pt: 1 }}>
                                <Button
                                    variant="contained"
                                    startIcon={<MailIcon />}
                                    href="mailto:press@mojopayr.com?subject=MojoPayr%20Press%20Inquiry"
                                    sx={{
                                        borderRadius: 999,
                                        textTransform: 'none',
                                        fontWeight: 900,
                                        bgcolor: ORANGE,
                                        '&:hover': { bgcolor: '#ff9c2a' },
                                    }}
                                >
                                    {isFr ? 'Contacter l equipe presse' : 'Contact press team'}
                                </Button>
                                <Button
                                    variant="outlined"
                                    endIcon={<ArrowForwardIcon />}
                                    onClick={() => navigate('/about')}
                                    sx={{
                                        borderRadius: 999,
                                        textTransform: 'none',
                                        fontWeight: 900,
                                        color: '#fff',
                                        borderColor: 'rgba(255,255,255,0.24)',
                                    }}
                                >
                                    {isFr ? 'Vue d ensemble de l entreprise' : 'Company overview'}
                                </Button>
                            </Stack>
                        </Stack>
                    </CardContent>
                </Card>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <Card sx={cardSx}>
                            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                                <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.2 }}>
                                    <CampaignIcon sx={{ color: ORANGE }} />
                                    <Typography sx={{ color: '#fff', fontWeight: 950, fontSize: 24 }}>
                                        {isFr ? 'Derniere annonce' : 'Latest announcement'}
                                    </Typography>
                                </Stack>

                                <Chip
                                    label={isFr ? 'Pour diffusion immediate' : 'For immediate release'}
                                    size="small"
                                    sx={{
                                        mb: 2,
                                        bgcolor: 'rgba(255,138,0,0.12)',
                                        color: ORANGE,
                                        fontWeight: 800,
                                    }}
                                />

                                <Typography sx={{ color: '#fff', fontWeight: 950, fontSize: 26, mb: 1 }}>
                                    {isFr
                                        ? 'MojoPayr renforce les paiements transfrontaliers avec un meilleur parcours diaspora vers l Afrique'
                                        : 'MojoPayr expands cross-border payments with a stronger diaspora-to-Africa flow'}
                                </Typography>

                                <Typography sx={{ color: 'rgba(255,255,255,0.62)', mb: 2.2 }}>
                                    {isFr ? 'Owings Mills, Maryland � 29 mars 2026' : 'Owings Mills, Maryland � March 29, 2026'}
                                </Typography>

                                <Stack spacing={1.6}>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.78)' }}>
                                        {isFr
                                            ? 'MojoPayr construit une maniere plus pratique pour les communautes diaspora et les utilisateurs locaux de transferer de l argent, payer des commerces, regler des factures, soutenir leurs familles, et gerer l activite financiere quotidienne a travers la Sierra Leone, le Liberia, la Guinee, et les marches voisins en croissance.'
                                            : 'MojoPayr is building a more practical way for diaspora communities and local users to move money, pay businesses, settle bills, support families, and manage day-to-day financial activity across Sierra Leone, Liberia, Guinea, and nearby growth markets.'}
                                    </Typography>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.78)' }}>
                                        {isFr
                                            ? 'Le produit est concu autour de la vraie maniere dont les gens operent : les utilisateurs diaspora paient depuis la devise de leur marche de residence avec un change et des frais clairs, tandis que les utilisateurs locaux beneficient d une tarification plus legere et plus adaptee au mobile money pour l usage quotidien.'
                                            : 'The product is designed around how people actually operate: diaspora users pay from their home-market currency with clear FX and fees, while local users get lighter, more mobile-money-friendly pricing for daily use.'}
                                    </Typography>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.78)' }}>
                                        {isFr
                                            ? 'MojoPayr rassemble aussi paiements directs, recus fiscaux, epargne de groupe, ticketing, dons, et outils marchands dans un seul ecosysteme afin que les utilisateurs n aient pas a dependre d une chaine d intermediaires pour accomplir des taches simples au pays.'
                                            : 'MojoPayr also brings direct payments, tax receipts, group savings, ticketing, donations, and merchant tools into one ecosystem so users do not have to rely on a chain of middle people to complete simple tasks back home.'}
                                    </Typography>
                                </Stack>

                                <Box sx={{ mt: 2.5 }}>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.56)', fontWeight: 900, mb: 1.2 }}>
                                        {isFr ? 'Angles media' : 'Media angles'}
                                    </Typography>
                                    <Stack spacing={1.1}>
                                        {mediaAngles.map((item) => (
                                            <Highlight key={item} text={item} />
                                        ))}
                                    </Stack>
                                </Box>

                                <Box sx={{ mt: 2.8 }}>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.56)', fontWeight: 900, mb: 1.2 }}>
                                        {isFr ? 'Presentation standard' : 'Boilerplate'}
                                    </Typography>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.76)' }}>
                                        {isFr
                                            ? 'MojoPayr est une plateforme fintech transfrontaliere orientee vers l aide aux communautes diaspora, utilisateurs locaux, et marchands pour faire circuler l argent plus directement a travers les corridors africains. La plateforme prend en charge les envois de type remittance, paiements commer�ants, factures, virements bancaires, dons, taxes, groupes de contribution, et operations marchandes avec des preuves plus claires et moins de friction.'
                                            : 'MojoPayr is a cross-border fintech platform focused on helping diaspora communities, local users, and merchants move money more directly across African corridors. The platform supports remittance-style sending, business payments, bills, bank transfers, donations, tax, contribution groups, and merchant operations with clearer proof and lower friction.'}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Stack spacing={3}>
                            <Card sx={cardSx}>
                                <CardContent sx={{ p: 3 }}>
                                    <Stack direction="row" spacing={1.1} alignItems="center" sx={{ mb: 1.4 }}>
                                        <VerifiedIcon sx={{ color: ORANGE }} />
                                        <Typography sx={{ color: '#fff', fontWeight: 950, fontSize: 22 }}>
                                            {isFr ? 'Faits presse' : 'Press facts'}
                                        </Typography>
                                    </Stack>

                                    <Stack spacing={1.1}>
                                        <Fact label={isFr ? 'Marches cles' : 'Core markets'} value="Sierra Leone, Liberia, Guinea, Gambia" />
                                        <Fact label={isFr ? 'Utilisateurs principaux' : 'Main users'} value={isFr ? 'Diaspora, consommateurs locaux, marchands' : 'Diaspora, local consumers, merchants'} />
                                        <Fact label={isFr ? 'Rails principaux' : 'Key rails'} value={isFr ? 'Portefeuille, mobile money, virement bancaire' : 'Wallet, mobile money, bank transfer'} />
                                        <Fact label={isFr ? 'Support' : 'Support'} value={isFr ? 'Email presse et centre d aide' : 'Press email and Help Center'} />
                                    </Stack>
                                </CardContent>
                            </Card>

                            <Card sx={cardSx}>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography sx={{ color: '#fff', fontWeight: 950, fontSize: 22, mb: 1.4 }}>
                                        {isFr ? 'Contact media' : 'Media contact'}
                                    </Typography>

                                    <Stack spacing={1}>
                                        <ContactRow label="Email" value="press@mojopayr.com" />
                                        <ContactRow label={isFr ? 'Support' : 'Support'} value="support@mojopayr.com" />
                                        <ContactRow label={isFr ? 'Base' : 'Base'} value="Owings Mills, Maryland" />
                                    </Stack>

                                    <Stack spacing={1.2} sx={{ mt: 2.3 }}>
                                        <Button
                                            variant="contained"
                                            startIcon={<MailIcon />}
                                            href="mailto:press@mojopayr.com?subject=MojoPayr%20Press%20Kit"
                                            sx={{
                                                borderRadius: 999,
                                                textTransform: 'none',
                                                fontWeight: 900,
                                                bgcolor: ORANGE,
                                                '&:hover': { bgcolor: '#ff9c2a' },
                                            }}
                                        >
                                            {isFr ? 'Demander le dossier de presse' : 'Request press kit'}
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            startIcon={<DownloadIcon />}
                                            onClick={() => navigate('/about')}
                                            sx={{
                                                borderRadius: 999,
                                                textTransform: 'none',
                                                fontWeight: 900,
                                                color: '#fff',
                                                borderColor: 'rgba(255,255,255,0.18)',
                                            }}
                                        >
                                            {isFr ? 'Voir la page entreprise' : 'View company page'}
                                        </Button>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Stack>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    )
}

export default Press
